import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Plus, Trash2, Target, TrendingDown, AlertTriangle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { calcOptionPoolImpact } from '@/utils/equityMath';
import type { OptionPoolRound, OptionPoolRoundResult } from '@/utils/equityMath';

interface PoolStrategy {
  id: string;
  name: string;
  color: string;
  rounds: RoundConfig[];
}

type RoundConfig = OptionPoolRound;

interface DilutionResult extends OptionPoolRoundResult {
  strategy: string;
}

/**
 * Chart series are keyed by strategy id rather than name, so two strategies
 * sharing a name cannot collapse into one series.
 */
const seriesKey = (strategy: PoolStrategy, suffix: string) => `s${strategy.id}_${suffix}`;

export default function OptionPoolImpactAnalyzer() {
  const [founderShares, setFounderShares] = useState(10000000);
  const [strategies, setStrategies] = useState<PoolStrategy[]>([
    {
      id: '1',
      name: 'Aggressive Pool (20%)',
      color: '#ef4444',
      rounds: [
        {
          roundName: 'Seed',
          newMoney: 2000000,
          preMoneyValuation: 8000000,
          poolSize: 20,
          poolTiming: 'pre',
          poolRefresh: true,
        },
        {
          roundName: 'Series A',
          newMoney: 10000000,
          preMoneyValuation: 30000000,
          poolSize: 15,
          poolTiming: 'pre',
          poolRefresh: true,
        },
        {
          roundName: 'Series B',
          newMoney: 25000000,
          preMoneyValuation: 75000000,
          poolSize: 12,
          poolTiming: 'pre',
          poolRefresh: true,
        },
      ],
    },
    {
      id: '2',
      name: 'Conservative Pool (10%)',
      color: '#22c55e',
      rounds: [
        {
          roundName: 'Seed',
          newMoney: 2000000,
          preMoneyValuation: 8000000,
          poolSize: 10,
          poolTiming: 'post',
          poolRefresh: true,
        },
        {
          roundName: 'Series A',
          newMoney: 10000000,
          preMoneyValuation: 30000000,
          poolSize: 10,
          poolTiming: 'post',
          poolRefresh: true,
        },
        {
          roundName: 'Series B',
          newMoney: 25000000,
          preMoneyValuation: 75000000,
          poolSize: 10,
          poolTiming: 'post',
          poolRefresh: true,
        },
      ],
    },
  ]);

  const [newStrategy, setNewStrategy] = useState({
    name: '',
    rounds: [] as RoundConfig[],
  });

  const [newRound, setNewRound] = useState<RoundConfig>({
    roundName: '',
    newMoney: 0,
    preMoneyValuation: 0,
    poolSize: 15,
    poolTiming: 'pre' as const,
    poolRefresh: true,
  });

  const impactOf = (strategy: PoolStrategy) => calcOptionPoolImpact(strategy.rounds, founderShares);

  const calculateDilution = (strategy: PoolStrategy): DilutionResult[] =>
    impactOf(strategy).rounds.map((round) => ({ ...round, strategy: strategy.name }));

  // Keyed by id: two strategies may legitimately share a name.
  const resultsByStrategy = new Map<string, DilutionResult[]>(
    strategies.map((strategy) => [strategy.id, calculateDilution(strategy)])
  );

  const poolWarnings = [...new Set(strategies.flatMap((s) => impactOf(s).warnings))];

  // The round axis is the union of every strategy's rounds, in first-seen order,
  // rather than whichever rounds the first strategy happened to define.
  const roundNames = ['Initial'];
  for (const strategy of strategies) {
    for (const round of strategy.rounds) {
      if (!roundNames.includes(round.roundName)) roundNames.push(round.roundName);
    }
  }

  const chartData = roundNames.map((roundName) => {
    const dataPoint: Record<string, string | number> = { round: roundName };
    for (const strategy of strategies) {
      const result = resultsByStrategy.get(strategy.id)?.find((r) => r.roundName === roundName);
      if (result) {
        dataPoint[seriesKey(strategy, 'founder')] = Number(result.founderOwnership.toFixed(2));
        dataPoint[seriesKey(strategy, 'pool')] = Number(result.optionPool.toFixed(2));
      }
    }
    return dataPoint;
  });

  const addStrategy = () => {
    if (newStrategy.name && newStrategy.rounds.length > 0) {
      setStrategies([
        ...strategies,
        {
          id: Date.now().toString(),
          name: newStrategy.name,
          color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
          rounds: [...newStrategy.rounds],
        },
      ]);
      setNewStrategy({ name: '', rounds: [] });
    }
  };

  const removeStrategy = (id: string) => {
    setStrategies(strategies.filter((s) => s.id !== id));
  };

  const addRoundToNewStrategy = () => {
    if (newRound.roundName && newRound.newMoney > 0 && newRound.preMoneyValuation > 0) {
      setNewStrategy({
        ...newStrategy,
        rounds: [...newStrategy.rounds, { ...newRound }],
      });
      setNewRound({
        roundName: '',
        newMoney: 0,
        preMoneyValuation: 0,
        poolSize: 15,
        poolTiming: 'pre',
        poolRefresh: true,
      });
    }
  };

  // Calculate dilution comparison metrics
  const finalResults = strategies.map((strategy) => {
    const results = calculateDilution(strategy);
    const initial = results[0];
    const final = results[results.length - 1];
    const totalDilution = initial.founderOwnership - final.founderOwnership;
    const avgPoolSize =
      strategy.rounds.reduce((sum, r) => sum + r.poolSize, 0) / strategy.rounds.length;
    const preMoneyRounds = strategy.rounds.filter((r) => r.poolTiming === 'pre').length;

    return {
      strategy: strategy.name,
      color: strategy.color,
      initialOwnership: initial.founderOwnership,
      finalOwnership: final.founderOwnership,
      totalDilution,
      avgPoolSize,
      preMoneyRounds,
      rounds: strategy.rounds.length,
    };
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Option Pool Impact Analyzer
          </CardTitle>
          <CardDescription>
            Compare how different option pool sizes and refresh strategies affect founder dilution
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Initial Setup */}
          <div className="space-y-4">
            <h3 className="font-semibold">Initial Capitalization</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Founder Shares</Label>
                <Input
                  type="number"
                  value={founderShares || ''}
                  onChange={(e) => setFounderShares(Math.max(0, Number(e.target.value)))}
                  min="0"
                  placeholder="10000000"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Add New Strategy */}
          <div className="space-y-4">
            <h3 className="font-semibold">Create New Strategy</h3>
            <div>
              <Label>Strategy Name</Label>
              <Input
                value={newStrategy.name}
                onChange={(e) => setNewStrategy({ ...newStrategy, name: e.target.value })}
                placeholder="e.g., Minimal Dilution Strategy"
              />
            </div>

            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <h4 className="text-sm font-medium">Add Funding Rounds</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <Label>Round Name</Label>
                  <Input
                    value={newRound.roundName}
                    onChange={(e) => setNewRound({ ...newRound, roundName: e.target.value })}
                    placeholder="Series A"
                  />
                </div>
                <div>
                  <Label>New Money ($)</Label>
                  <Input
                    type="number"
                    value={newRound.newMoney || ''}
                    onChange={(e) =>
                      setNewRound({ ...newRound, newMoney: Math.max(0, Number(e.target.value)) })
                    }
                    min="0"
                    placeholder="10000000"
                  />
                </div>
                <div>
                  <Label>Pre-Money Val ($)</Label>
                  <Input
                    type="number"
                    value={newRound.preMoneyValuation || ''}
                    onChange={(e) =>
                      setNewRound({
                        ...newRound,
                        preMoneyValuation: Math.max(0, Number(e.target.value)),
                      })
                    }
                    placeholder="30000000"
                  />
                </div>
                <div>
                  <Label>Pool Size (%)</Label>
                  <Input
                    type="number"
                    value={newRound.poolSize || ''}
                    onChange={(e) =>
                      setNewRound({
                        ...newRound,
                        poolSize: Math.min(99, Math.max(0, Number(e.target.value))),
                      })
                    }
                    min="0"
                    max="99"
                    placeholder="15"
                  />
                </div>
                <div>
                  <Label>Pool Timing</Label>
                  <Select
                    value={newRound.poolTiming}
                    onValueChange={(value: RoundConfig['poolTiming']) =>
                      setNewRound({ ...newRound, poolTiming: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pre">Pre-Money</SelectItem>
                      <SelectItem value="post">Post-Money</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={addRoundToNewStrategy} variant="outline" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {newStrategy.rounds.length > 0 && (
                <div className="space-y-2">
                  {newStrategy.rounds.map((round, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-background rounded border"
                    >
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-medium">{round.roundName}</span>
                        <span className="text-muted-foreground">
                          ${(round.newMoney / 1000000).toFixed(1)}M @ $
                          {(round.preMoneyValuation / 1000000).toFixed(1)}M
                        </span>
                        <Badge variant="outline">
                          {round.poolSize}% ({round.poolTiming})
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setNewStrategy({
                            ...newStrategy,
                            rounds: newStrategy.rounds.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={addStrategy}
              disabled={!newStrategy.name || newStrategy.rounds.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Strategy
            </Button>
          </div>

          <Separator />

          {/* Current Strategies */}
          <div className="space-y-2">
            <h3 className="font-semibold">Comparison Strategies</h3>
            {strategies.map((strategy) => (
              <div
                key={strategy.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: strategy.color }} />
                  <div>
                    <div className="font-medium">{strategy.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {strategy.rounds.length} rounds
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeStrategy(strategy.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {strategies.length > 0 && (
        <>
          {poolWarnings.length > 0 && (
            <Card className="border-destructive/50">
              <CardContent className="pt-6 space-y-2">
                {poolWarnings.map((warning, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">{warning}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Comparison Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Strategy Comparison</CardTitle>
              <CardDescription>Final founder ownership and dilution impact</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Strategy</TableHead>
                    <TableHead className="text-right">Initial Ownership</TableHead>
                    <TableHead className="text-right">Final Ownership</TableHead>
                    <TableHead className="text-right">Total Dilution</TableHead>
                    <TableHead className="text-right">Avg Pool Size</TableHead>
                    <TableHead>Pre-Money Rounds</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finalResults.map((result) => (
                    <TableRow key={result.strategy}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: result.color }}
                          />
                          <span className="font-medium">{result.strategy}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {result.initialOwnership.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {result.finalOwnership.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={result.totalDilution > 70 ? 'destructive' : 'secondary'}>
                          -{result.totalDilution.toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{result.avgPoolSize.toFixed(1)}%</TableCell>
                      <TableCell>
                        {result.preMoneyRounds} of {result.rounds}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Founder Ownership Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Founder Ownership Over Time</CardTitle>
              <CardDescription>How founder equity evolves across funding rounds</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="round" />
                  <YAxis label={{ value: 'Ownership %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  {strategies.map((strategy) => (
                    <Line
                      key={strategy.id}
                      type="monotone"
                      dataKey={seriesKey(strategy, 'founder')}
                      stroke={strategy.color}
                      strokeWidth={3}
                      name={strategy.name}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Option Pool Size Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Option Pool Size Over Time</CardTitle>
              <CardDescription>Pool size as percentage of fully diluted cap table</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="round" />
                  <YAxis label={{ value: 'Pool Size %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  {strategies.map((strategy) => (
                    <Bar
                      key={strategy.id}
                      dataKey={seriesKey(strategy, 'pool')}
                      fill={strategy.color}
                      name={strategy.name}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detailed Round-by-Round Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Round Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={strategies[0]?.id || ''}>
                <TabsList className="w-full">
                  {strategies.map((strategy) => (
                    <TabsTrigger key={strategy.id} value={strategy.id} className="flex-1">
                      {strategy.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {strategies.map((strategy) => (
                  <TabsContent key={strategy.id} value={strategy.id} className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Round</TableHead>
                          <TableHead>Pool Strategy</TableHead>
                          <TableHead className="text-right">Founder %</TableHead>
                          <TableHead className="text-right">Option Pool %</TableHead>
                          <TableHead className="text-right">Investor %</TableHead>
                          <TableHead className="text-right">Price/Share</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resultsByStrategy[strategy.name]?.map((result, idx) => {
                          const round = strategy.rounds[idx - 1]; // -1 because first result is "Initial"
                          return (
                            <TableRow key={result.roundName}>
                              <TableCell className="font-medium">{result.roundName}</TableCell>
                              <TableCell>
                                {round ? (
                                  <Badge variant="outline">
                                    {round.poolSize}% ({round.poolTiming})
                                  </Badge>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {result.founderOwnership.toFixed(2)}%
                              </TableCell>
                              <TableCell className="text-right">
                                {result.optionPool.toFixed(2)}%
                              </TableCell>
                              <TableCell className="text-right">
                                {result.investorOwnership.toFixed(2)}%
                              </TableCell>
                              <TableCell className="text-right">
                                {result.pricePerShare > 0
                                  ? `$${result.pricePerShare.toFixed(4)}`
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Strategic Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Pool Strategy Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Pre-Money vs Post-Money Pool Timing
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Pre-money pools dilute founders before investors arrive, increasing founder
                  dilution. Post-money pools are calculated after the investment, sharing dilution
                  with new investors.
                </p>
              </div>

              {finalResults.some((r) => r.totalDilution > 70) && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    High Dilution Warning
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Some strategies result in over 70% founder dilution. Consider negotiating
                    smaller pools or post-money timing to preserve more equity.
                  </p>
                </div>
              )}

              {finalResults.some((r) => r.avgPoolSize > 15) && (
                <div className="p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <TrendingDown className="h-4 w-4" />
                    Large Pool Sizes
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Average pool sizes over 15% are generous and increase dilution. Ensure hiring
                    plans justify these allocations, or negotiate for smaller pools.
                  </p>
                </div>
              )}

              <div className="p-3 bg-muted/50 border rounded-lg">
                <p className="text-sm font-medium">Best Practice Recommendations</p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                  <li>Target 10-15% option pools for most stages</li>
                  <li>Negotiate for post-money pools when possible</li>
                  <li>Only refresh pools when necessary for hiring needs</li>
                  <li>Consider existing pool size before adding new shares</li>
                  <li>Balance employee retention needs with founder dilution</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
